/**
 * The drawings.
 *
 * All of it is decorative. Nothing here may be the only carrier of a fact —
 * a tile still says "Ice cream $6.00" in text, the empty gauge still explains
 * itself in words — so every piece is `aria-hidden` and none of it is ever
 * the thing a family reads. Themed vocabulary is display-only and cannot
 * change a financial value (CLAUDE.md, Invariants); pictures are the same rule
 * one step further out.
 *
 * Each drawing ships twice: once as drawn, and once with the near-black crayon
 * outline lifted to a pale green for dark mode, where the original disappears
 * into the ground. The swap is CSS, not JS, so it follows the viewer's theme
 * in all three of its states — explicit light, explicit dark, and the
 * unstamped default where only `prefers-color-scheme` separates them. See
 * `scripts/build-art.sh` for how the two variants are generated.
 */

import iceCream from '../assets/art/tiles/ice-cream.webp';
import iceCreamDark from '../assets/art/tiles/ice-cream-dark.webp';
import ticket from '../assets/art/tiles/ticket.webp';
import ticketDark from '../assets/art/tiles/ticket-dark.webp';
import pizza from '../assets/art/tiles/pizza.webp';
import pizzaDark from '../assets/art/tiles/pizza-dark.webp';
import drink from '../assets/art/tiles/drink.webp';
import drinkDark from '../assets/art/tiles/drink-dark.webp';
import popcorn from '../assets/art/tiles/popcorn.webp';
import popcornDark from '../assets/art/tiles/popcorn-dark.webp';
import football from '../assets/art/tiles/football.webp';
import footballDark from '../assets/art/tiles/football-dark.webp';
import paints from '../assets/art/tiles/paints.webp';
import paintsDark from '../assets/art/tiles/paints-dark.webp';
import bus from '../assets/art/tiles/bus.webp';
import busDark from '../assets/art/tiles/bus-dark.webp';

import fuelSpot from '../assets/art/fuel-spot.webp';
import fuelSpotDark from '../assets/art/fuel-spot-dark.webp';
import emptyState from '../assets/art/empty-state.webp';
import emptyStateDark from '../assets/art/empty-state-dark.webp';

import './Art.css';

const TILE_ICONS = {
  'ice-cream': [iceCream, iceCreamDark],
  ticket: [ticket, ticketDark],
  pizza: [pizza, pizzaDark],
  drink: [drink, drinkDark],
  popcorn: [popcorn, popcornDark],
  football: [football, footballDark],
  paints: [paints, paintsDark],
  bus: [bus, busDark],
};

/**
 * The pickable set, in the order a parent should see it.
 *
 * Exported so the tile editor can offer the drawings that exist rather than
 * hard-coding a second list beside this one — a picker showing a key with no
 * drawing behind it renders an empty box, and the parent has no way to tell
 * that from a drawing that failed to load.
 */
export const TILE_ICON_KEYS = Object.keys(TILE_ICONS);

export const SPOTS = {
  fuel: [fuelSpot, fuelSpotDark],
  empty: [emptyState, emptyStateDark],
};

/**
 * Label to drawing.
 *
 * A tile's label is whatever the parent typed, so this is a guess, and a wrong
 * guess is worse than none — a bus next to "Ice cream" makes the tile harder
 * to find at a glance, not easier, and the whole point of the tile is that it
 * is found without reading. So an unmatched label gets no picture at all
 * rather than a stand-in, and the tiles are designed to look right with a
 * mixture of the two.
 *
 * Ordered: "takeaway pizza" should land on pizza, and it would match either.
 */
const KEYWORDS = [
  [/ice ?cream|gelato|sundae|99/, 'ice-cream'],
  [/pizza|takeaway|takeout|chippy|burger/, 'pizza'],
  [/shake|smoothie|drink|soda|juice|boba|slush|hot choc/, 'drink'],
  [/cinema|movie|film|ticket|show|theatre|theater|panto/, 'ticket'],
  [/popcorn|snack|treat|sweets|candy|chocolate/, 'popcorn'],
  [/football|soccer|ball|match|sport|bowling|swim/, 'football'],
  [/paint|craft|art|museum|pottery|lego|book/, 'paints'],
  [/bus|trip|outing|store run|shops|travel|zoo|park|day out/, 'bus'],
];

/** The pair of sources for a tile, or null when nothing matches confidently. */
export function tileIconFor(tile) {
  // An explicit `icon` on the tile row wins outright — it is a choice someone
  // made, and the keyword guess below is only ever a fallback for tiles that
  // predate anyone making one.
  if (tile?.icon && TILE_ICONS[tile.icon]) return TILE_ICONS[tile.icon];

  const label = (tile?.label ?? '').toLowerCase();
  for (const [pattern, key] of KEYWORDS) {
    if (pattern.test(label)) return TILE_ICONS[key];
  }
  return null;
}

/**
 * A drawing rendered as a background image, not an `<img>`.
 *
 * Both variants are handed to CSS as custom properties and CSS picks one. An
 * `<img>` would need the theme in JS, which means reading it at render time
 * and re-reading it when the OS setting changes mid-session — a listener, a
 * state, and a re-render, to choose between two files that differ only in
 * colour.
 */
function Drawing({ sources, className }) {
  if (!sources) return null;
  const [light, dark] = sources;
  return (
    <span
      className={className}
      aria-hidden="true"
      style={{ '--art': `url(${light})`, '--art-dark': `url(${dark})` }}
    />
  );
}

export function TileIcon({ tile }) {
  return <Drawing sources={tileIconFor(tile)} className="tile__icon" />;
}

/**
 * A larger drawing for a screen that has nothing else on it yet.
 *
 * The modifier class carries the drawing's own shape — they were not all drawn
 * at the same aspect ratio, and a 3:2 drawing in a square box is a third of a
 * box of nothing, which on a card reads as a layout bug rather than as space.
 */
export function SpotArt({ name }) {
  return <Drawing sources={SPOTS[name]} className={`spot-art spot-art--${name}`} />;
}
